import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ReservationFeedbackBadgeProps {
  count: number;
  rating: number | null;
  to?: string;
}

const getReservationFeedbackBadgeTone = (
  count: number,
  rating: number | null,
) => {
  if (count === 0 || rating === null) {
    return "bg-gray-5 text-white";
  }

  if (rating >= 5) {
    return "bg-light-green text-white";
  }

  if (rating >= 4) {
    return "bg-light-orange text-white";
  }

  return "bg-dark-orange text-white";
};

export const ReservationFeedbackBadge: React.FC<
  ReservationFeedbackBadgeProps
> = ({ count, rating, to }) => {
  const { t } = useTranslation();

  const badgeLabel =
    count > 0
      ? t("reservation.table.feedbackIndicator", { count })
      : t("reservation.table.feedbackIndicatorEmpty");
  const isInteractive = Boolean(to) && count > 0;
  const className = cn(
    "mx-auto h-6 min-w-6 justify-center rounded-md border-transparent px-2 text-xs font-semibold",
    getReservationFeedbackBadgeTone(count, rating),
    isInteractive && "cursor-pointer transition-opacity hover:opacity-90",
  );

  if (isInteractive && to) {
    return (
      <Badge asChild className={className}>
        <Link
          to={to}
          onClick={(event) => event.stopPropagation()}
          aria-label={badgeLabel}
        >
          <span aria-hidden>{count}</span>
        </Link>
      </Badge>
    );
  }

  return (
    <Badge className={className} aria-label={badgeLabel}>
      <span aria-hidden>{count}</span>
    </Badge>
  );
};
